import {
  type AgentRun,
  type AgentRunStatus,
  AgentRunStatusValues,
  type CreateAgentRunOptions,
  type SortParam,
} from '@lucidtech/las-sdk-browser';
import { useInfiniteQuery } from '@tanstack/react-query';
import { useDebounce } from '@uidotdev/usehooks';
import { useCallback, useEffect, useMemo } from 'react';

import { useClient } from '@/hooks';
import { create } from '@/hooks/api';
import { DateColumnFilter, DateRangeColumnFilter, useInboxStore } from '@/store';
import { isAgentRunId, isArray, JSONType, TokenCredentials } from '@/utils';

export type AgentRunId = { agentId: string; runId: string };

export const {
  getOptions: getAgentRunOptions,
  useBatchGet: useBatchGetAgentRun,
  useCreate: useCreateAgentRun,
  useDelete: useDeleteAgentRun,
  useGet: useGetAgentRun,
  useList: useListAgentRuns,
  useInvalidateQueries: useInvalidateAgentRunQueries,
  useUpdate: useUpdateAgentRun,
} = create<AgentRun, AgentRunId, CreateAgentRunOptions & { agentId: string }>({
  cacheKey: 'agentRuns',
  equal: (agentRun, id) =>
    Boolean(agentRun && id?.agentId && id?.runId && agentRun.agentId === id.agentId && agentRun.runId === id.runId),
  createId: (agentRun) => (agentRun ? { agentId: agentRun.agentId, runId: agentRun.runId } : null),
  create: (client, run) => client.createAgentRun(run.agentId, { ...run, agentId: undefined }),
  get: (client, id) => client.getAgentRun(id.agentId, id.runId),
  delete: (client, id) => client.deleteAgentRun(id.agentId, id.runId),
  list: (client, options) => {
    return new Promise((resolve, reject) => {
      client
        .listAgentRuns(options.agentId, { ...options, agentId: undefined })
        .then((response: JSONType) => {
          resolve({ data: response.runs, nextToken: response.nextToken });
        })
        .catch((error) => {
          reject(error);
        });
    });
  },
  update: (client, id, updates) => client.updateAgentRun(id.agentId, id.runId, updates),
});

export const toAgentRunId = (agentId?: string | null, runId?: string | null): AgentRunId | null => {
  if (isAgentRunId(runId)) {
    const [_agentId, _runId] = runId.split('/');
    return { agentId: _agentId, runId: _runId };
  }
  return agentId && runId ? { agentId, runId } : null;
};

export type UseFilteredAgentRunsQueryOpts = {
  agentId?: string;
};

export const useFilteredAgentRunsQuery = ({ agentId }: UseFilteredAgentRunsQueryOpts) => {
  const sorting = useInboxStore((state) => state.sorting);
  const filters = useInboxStore((state) => state.filters);
  const activeFilters = useMemo(() => filters.filter((f) => f.id && f.type && f.value), [filters]);
  const search = useInboxStore((state) => state.search);
  const debouncedSearch = useDebounce(search, 500);

  const { client } = useClient();
  const cacheKey = useMemo(() => {
    const organizationId = (client.credentials as TokenCredentials).organizationId;
    return [organizationId, 'agentRuns', 'list', agentId, sorting, debouncedSearch, activeFilters];
  }, [activeFilters, agentId, client.credentials, debouncedSearch, sorting]);

  const queryFn = useCallback(
    async ({ pageParam }: { pageParam?: string }) => {
      if (!agentId) throw new Error('Cannot list runs for nullish agentId');

      const sort: SortParam<AgentRun>[] = [];
      for (const sortOption of sorting) {
        if (!sortOption.hidden) {
          const { id, desc } = sortOption as { id: keyof AgentRun; desc: boolean };
          sort.push({ column: id, order: desc ? 'desc' : 'asc' });
        }
      }

      const isAgentRunIdStatus = (s: string): s is AgentRunStatus => {
        const statusValues: string[] = [...AgentRunStatusValues];
        return statusValues.includes(s);
      };
      const status: AgentRunStatus[] = [];
      for (const activeFilter of activeFilters.filter((f) => f.id === 'status' && f.condition === 'is')) {
        if (isArray(activeFilter.value)) {
          status.push(...activeFilter.value.map((v) => v.id).filter(isAgentRunIdStatus));
        }
      }

      const dateFilters: Record<string, Record<string, Date | undefined>> = {
        createdTime: { after: undefined, before: undefined },
        updatedTime: { after: undefined, before: undefined },
      };

      const compareDates = (condition: 'after' | 'before', newDate: Date, oldDate?: Date) => {
        switch (condition) {
          case 'after':
            return newDate >= (oldDate ?? newDate) ? newDate : oldDate;
          case 'before':
            return newDate >= (oldDate ?? newDate) ? newDate : oldDate;
        }
      };

      for (const activeFilter of activeFilters.filter((f) => f.type === 'date')) {
        const filter = activeFilter as DateColumnFilter;
        if (!filter.id || !filter.condition || !filter.value) continue;
        const date = filter.value.toDate('UTC');
        dateFilters[filter.id][filter.condition] = compareDates(
          filter.condition,
          date,
          dateFilters[filter.id][filter.condition]
        );
      }

      for (const activeFilter of activeFilters.filter((f) => f.type === 'date-range')) {
        const filter = activeFilter as DateRangeColumnFilter;
        if (!filter.id || !filter.condition || !filter.value) continue;
        const fromDate = filter.value.start.toDate('UTC');
        dateFilters[filter.id]['after'] = compareDates('after', fromDate, dateFilters[filter.id]['after']);
        const toDate = filter.value.end.toDate('UTC');
        dateFilters[filter.id]['before'] = compareDates('before', toDate, dateFilters[filter.id]['before']);
      }

      const apiFilters: Record<string, Date> = {};
      for (const [column, dateRange] of Object.entries(dateFilters)) {
        for (const [range, date] of Object.entries(dateRange)) {
          if (date) {
            const when = range.charAt(0).toUpperCase() + range.slice(1);
            apiFilters[`${column}${when}`] = date;
          }
        }
      }

      const runs = await client.listAgentRuns(agentId, {
        nextToken: pageParam,
        maxResults: 50,
        history: debouncedSearch,
        status,
        sort,
        ...apiFilters,
      });

      return {
        data: runs.runs,
        nextToken: runs.nextToken,
      };
    },
    [activeFilters, agentId, client, debouncedSearch, sorting]
  );

  const { isFetching, data, ...rest } = useInfiniteQuery({
    queryKey: cacheKey,
    queryFn: queryFn,
    // TODO replace '' with undefined: Breaks TS type inference
    initialPageParam: '',
    getNextPageParam: (lastPage) => lastPage.nextToken,
    refetchOnWindowFocus: false,
    refetchInterval: 2000,
    staleTime: 5 * 60 * 1000,
    enabled: agentId != null,
  });

  const setIsFetching = useInboxStore((state) => state.setIsFetching);

  useEffect(() => {
    setIsFetching(true);
  }, [setIsFetching, sorting, activeFilters, debouncedSearch]);

  useEffect(() => {
    if (!isFetching || data) setTimeout(() => setIsFetching(false), 500);
  }, [isFetching, data, setIsFetching]);

  return { isFetching, data, ...rest };
};
